import { useTranslation } from 'react-i18next';
import { topPoiBadges, type PoiBadge } from '@/hooks/useSourcePosts';
import { WarsawMetroIcon } from '@/components/offer/WarsawMetroBadge';
import { IKONA_FILTRA_POI } from '@/components/search/IkonyFiltrowOkolicy';

interface Props {
  walkability: number | null | undefined;
  transit: number | null | undefined;
  green: number | null | undefined;
  badges: string[] | null | undefined;
  metroNearestName?: string | null;
  /** 'lg' = larger pills/icons on desktop (offer page). 'sm' = SERP cards (default). */
  size?: 'sm' | 'lg';
}

/** Pill sizing: 'lg' grows on desktop only (mobile stays compact). */
const pillCls = (big: boolean) =>
  big ? 'px-2 py-0.5 text-[11px] lg:px-3 lg:py-1 lg:text-sm' : 'px-2 py-0.5 text-[11px]';

const SCORE_BG = (n: number | null | undefined): string => {
  if (n == null) return 'bg-muted text-muted-foreground';
  if (n >= 85) return 'bg-emerald-100 text-emerald-900';
  if (n >= 65) return 'bg-lime-100 text-lime-900';
  if (n >= 45) return 'bg-amber-100 text-amber-900';
  return 'bg-orange-100 text-orange-900';
};

/**
 * Wynik okolicy: liniowa ikona + liczba. Ikony TE SAME co filtry i zakładki okolicy
 * (`IKONA_FILTRA_POI`) — do 30.09 były tu emoji (pieszy, metro, drzewo), jedyne w karcie, które inaczej
 * wyglądały na każdym systemie. Emoji niosło też znaczenie dla czytnika ekranu; ikona jest
 * `aria-hidden`, więc nazwę wyniku daje ukryty tekst (a `title` — podpowiedź myszą).
 */
export type RodzajWyniku = 'walkability' | 'transit' | 'green';
const IKONA_WYNIKU = {
  walkability: IKONA_FILTRA_POI.walk,
  transit: IKONA_FILTRA_POI.transit,
  green: IKONA_FILTRA_POI.green,
} as const;

function ScorePill({ rodzaj, value, big }: { rodzaj: RodzajWyniku; value: number | null | undefined; big: boolean }) {
  const { t } = useTranslation();
  const Ikona = IKONA_WYNIKU[rodzaj];
  const nazwa = t(`search.poi.scores.${rodzaj}`);
  return (
    <div
      data-wynik-okolicy={rodzaj}
      title={nazwa}
      className={`inline-flex items-center gap-1 rounded-full font-medium ${pillCls(big)} ${SCORE_BG(value)}`}
    >
      <Ikona className={`shrink-0 opacity-80 ${big ? 'h-3 w-3 lg:h-3.5 lg:w-3.5' : 'h-3 w-3'}`} aria-hidden />
      <span className="sr-only">{nazwa}</span>
      <span className="tabular-nums font-semibold">{value ?? '–'}</span>
    </div>
  );
}

function BadgeChip({ badge, metroName, big }: { badge: PoiBadge; metroName?: string | null; big: boolean }) {
  const { t } = useTranslation();
  if (badge === 'near_metro') {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full bg-secondary font-medium text-secondary-foreground ${pillCls(big)}`}>
        <WarsawMetroIcon size={big ? 14 : 12} />
        {metroName
          ? t('search.poi.badges.near_metro_named', { station: metroName })
          : t('search.poi.badges.near_metro')}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center rounded-full bg-secondary font-medium text-secondary-foreground ${pillCls(big)}`}>
      {t(`search.poi.badges.${badge}`)}
    </span>
  );
}

export function PoiBadgeStrip({ walkability, transit, green, badges, metroNearestName, size = 'sm' }: Props) {
  const top: PoiBadge[] = topPoiBadges(badges, 3);
  const noScores = walkability == null && transit == null && green == null;
  if (noScores && top.length === 0) return null;
  const big = size === 'lg';

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${big ? 'lg:gap-2' : ''}`}>
      {!noScores && (
        <>
          <ScorePill rodzaj="walkability" value={walkability} big={big} />
          <ScorePill rodzaj="transit" value={transit} big={big} />
          <ScorePill rodzaj="green" value={green} big={big} />
        </>
      )}
      {top.map((b) => (
        <BadgeChip key={b} badge={b} metroName={metroNearestName} big={big} />
      ))}
    </div>
  );
}
